import React, { useEffect, useRef, useState } from "react";
import { Space, Table, Button, Input, message, Drawer, Form, Row, Col, Tag, Divider, Select, ConfigProvider, Tabs, Spin, Modal } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, CloseOutlined, UserOutlined, MailOutlined, PhoneOutlined, LockOutlined, GlobalOutlined, CheckOutlined } from "@ant-design/icons";
import Column from "antd/es/table/Column";
import { useAddAdminUser, useGetAllPermission, useFetchAdminUserList, useDeleteAdminUserPermanently, useGetAllRoles } from "../services/user.service";
import { AdminUserUpsertDto, IUserRoles } from "../types/user";
import { storage } from "../utils/storage";
import { useNavigate } from "react-router-dom";
import { Checkbox } from "antd";
import { useFetchCountry } from '../services/countries.service';
import { useFetchStates } from '../services/states.service';
import { useFetchCities } from '../services/cities.service';
import { isNotNullUndefined, isNotNullUndefinedBlank, formatDateTime } from "../helpers/common";
import Pagination from "../helpers/pagination";

// Permissions Selector Component
interface PermissionsSelectorProps {
    value?: number[];
    onChange?: (value: number[]) => void;
    groupedPermissions: any;
}

const PermissionsSelector: React.FC<PermissionsSelectorProps> = ({ value = [], onChange, groupedPermissions }) => {
    const allPermissionValues = Object.values(groupedPermissions).flatMap((perms: any) =>
        perms.map((p: any) => p.value)
    );

    const isAllSelected = allPermissionValues.length > 0 && allPermissionValues.every((v) => value.includes(v));

    const handleSelectAll = () => {
        if (isAllSelected) {
            onChange?.([]); // Deselect all
        } else {
            onChange?.(allPermissionValues); // Select all
        }
    };

    return (
        <div>
            {/* Select All Button */}
            <div style={{ marginBottom: 12, display: "flex", justifyContent: "flex-end" }}>
                <Button
                    type={isAllSelected ? "default" : "primary"}
                    className={
                        isAllSelected
                            ? "hover:!border-black hover:!text-black"
                            : "bg-black text-white hover:!border-black hover:!bg-transparent hover:!text-black"
                    }
                    onClick={handleSelectAll}
                    icon={isAllSelected ? <CloseOutlined /> : <CheckOutlined />}
                >
                    {isAllSelected ? "Deselect All" : "Select All"}
                </Button>
            </div>

            {Object.keys(groupedPermissions).map((module, index) => {
                const modulePermissions = groupedPermissions[module];
                const moduleValues = modulePermissions.map((p: any) => p.value);
                const selectedInModule = value.filter((v: number) => moduleValues.includes(v));

                const isModuleAllSelected = moduleValues.every((v: number) => value.includes(v));

                const handleModuleSelectAll = () => {
                    if (isModuleAllSelected) {
                        // Deselect all in this module
                        onChange?.(value.filter((v) => !moduleValues.includes(v)));
                    } else {
                        // Select all in this module
                        const merged = Array.from(new Set([...value, ...moduleValues]));
                        onChange?.(merged);
                    }
                };

                return (
                    <div key={index} style={{ marginBottom: 16, border: "1px solid #f0f0f0", borderRadius: 8, padding: 12 }}>
                        {/* Module Header */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ fontWeight: 600, textTransform: "capitalize" }}>
                                    {module.replaceAll("_", " ")}
                                </span>
                                <Tag color="blue">
                                    {selectedInModule.length} of {modulePermissions.length} selected
                                </Tag>
                            </div>
                            {/* Per-module Select All */}
                            <Button
                                size="small"
                                type="link"
                                style={{ color: "black", textDecoration: "underline", padding: 0 }}
                                onClick={handleModuleSelectAll}
                            >
                                {isModuleAllSelected ? "Deselect All" : "Select All"}
                            </Button>
                        </div>

                        {/* Checkboxes */}
                        <Checkbox.Group
                            value={value}
                            onChange={(checked) => {
                                const otherValues = value.filter((v) => !moduleValues.includes(v));
                                onChange?.([...otherValues, ...(checked as number[])]);
                            }}
                        >
                            <Row gutter={[8, 8]}>
                                {modulePermissions.map((permission: any) => (
                                    <Col span={12} key={permission.value}>
                                        <Checkbox value={permission.value}>{permission.label}</Checkbox>
                                    </Col>
                                ))}
                            </Row>
                        </Checkbox.Group>
                    </div>
                );
            })}
        </div>
    );
};
const AdminUsers: React.FC = () => {
    const { Search } = Input;
    const navigate = useNavigate();
    const { TabPane } = Tabs;

    const [open, setOpen] = useState(false);
    const [admins, setAdmins] = useState<any[]>([]);
    const [userRoles, setUserRoles] = useState<IUserRoles[]>([]);
    const [form] = Form.useForm();
    const [editingAdmin, setEditingAdmin] = useState<AdminUserUpsertDto | null>(null);
    const [groupedPermissions, setGroupedPermissions] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [formValues, setFormValues] = useState<AdminUserUpsertDto | null>(null);
    const [modalType, setModalType] = useState<'add' | 'update' | ''>('');

    // Pagination states
    const pageNumber = useRef(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const searchValue = useRef<string>("");

    const filters = useRef({ statusFilter: 'All' });
    const [adminStatuses] = useState<any[]>([
        { statusId: 0, statusName: 'All' },
        { statusId: 1, statusName: 'Active' },
        { statusId: 100, statusName: 'Deactivated' }
    ]);

    // Country, State, City states
    const [countries, setCountries] = useState<any[]>([]);
    const [stateOptions, setStateOptions] = useState<any[]>([]);
    const [cityOptions, setCityOptions] = useState<any[]>([]);
    const [selectedCountry, setSelectedCountry] = useState<any>(null);
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [showPermissions, setShowPermissions] = useState(false);

    const deleteAdminMutation = useDeleteAdminUserPermanently({
        onSuccess: (response: any) => {
            if (response?.statusCode !== 200) {
                message.error(response?.statusMessage);
                return;
            }

            message.success(response?.statusMessage || "Admin deleted successfully");
            setDeleteModalVisible(false);
            setDeleteId(null);
            fetchAdminList();
        },
        onError: (err: any) => {
            message.error(err?.response?.data?.statusMessage || "Delete failed");
        }
    });

    const fetchAdminList = () => {
        setLoading(true);
        fetchAdminListMutation.mutate({
            statusFilter: filters.current.statusFilter,
            searchFilter: searchValue.current ?? "",
            pageNumber: pageNumber.current,
            pageSize: pageSize
        });
    };

    const fetchAdminListMutation = useFetchAdminUserList({
        onSuccess: (res: any) => {
            if (res?.statusCode === 200) {
                const formattedData = res.data.map((data: any) => ({
                    ...data,
                    adminUserId: data.adminUserID,
                    createdAt: formatDateTime(data.createdAt, "DD/MM/YYYY HH:mm AMPM"),
                    statusName: [data.statusName || "Active"]
                }));
                setAdmins(formattedData);
                const recordCount = res.data?.[0]?.recordCount ?? 0;
                setTotalPages(Math.ceil(recordCount / pageSize));
            } else {
                setAdmins([]);
                setTotalPages(1);
            }
            setLoading(false);
        },
        onError: (err: any) => {
            setLoading(false);
            message.error("Failed to load admin users");
        }
    });

    const addAdminMutation = useAddAdminUser({
        onSuccess: (response: any) => {
            setLoading(false);
            if (response?.statusCode !== 200) {
                message.error(response?.statusMessage);
                return;
            }
            message.success(response?.statusMessage);
            setIsModalVisible(false);
            setOpen(false);
            form.resetFields();
            pageNumber.current = 1;
            fetchAdminList();
        },
        onError: (err: any) => {
            setLoading(false);
            message.error(err?.response?.data?.statusMessage || "Server error");
        }
    });

    const fetchUserRolesListMutation = useGetAllRoles({
        onSuccess: (res: any) => {
            if (res?.statusCode === 200) {
                setUserRoles(res.data);
            } else {
                setUserRoles([]);
            }
            setLoading(false);
        },
        onError: (err: any) => {
            setLoading(false);
            message.error("Failed to load user roles");
        }
    });

    const fetchUserRoles = () => {
        fetchUserRolesListMutation.mutate({});
    }

    const confirmDelete = () => {
        if (deleteId) {
            deleteAdminMutation.mutate(deleteId);
        }
    };


    // Fetch Countries
    const fetchCountries = () => {
        if (countries.length <= 0) {
            fetchCountryMutation.mutate({
                searchFilter: "",
                isServiceableAttendant: "",
                isServiceableTraveler: ""
            });
        }
    };

    const fetchCountryMutation = useFetchCountry({
        onSuccess: (response: any) => {
            if (response != null && response !== undefined) {
                setCountries(response);
            }
        },
        onError: (error: any) => {
            message.error(error.message);
        },
    });

    // Handle Country Change
    const onCountryChange = async (countryId: string = "") => {
        setCityOptions([]);
        setStateOptions([]);
        form.setFieldValue("state", null);
        form.setFieldValue("city", null);
        if (isNotNullUndefinedBlank(countryId)) {
            fetchStateMutation.mutate({
                countryId: countryId,
                searchFilter: ""
            });
        }
    };

    const onRoleChange = async (roleId: number) => {
        if (roleId === 1) {
            setShowPermissions(false);
        } else {
            setShowPermissions(true);
        }
    };

    const fetchStateMutation = useFetchStates({
        onSuccess: (response: any) => {
            if (isNotNullUndefined(response)) {
                setStateOptions(response.data);
            }
        },
        onError: (error) => {
            message.error(error.message);
        },
    });

    // Handle State Change
    const onStateChange = async (stateId: string = "") => {
        setCityOptions([]);
        form.setFieldValue("city", null);
        const countryId = form.getFieldValue("country");
        if (isNotNullUndefinedBlank(stateId) && isNotNullUndefinedBlank(countryId)) {
            fetchCityMutation.mutate({
                stateId: stateId,
                countryId: countryId,
                searchFilter: "",
                isFavourite: "",
                isServiceableAttendant: "",
                isServiceableTraveler: "",
            });
        }
    };

    const fetchCityMutation = useFetchCities({
        onSuccess: (response: any) => {
            if (isNotNullUndefined(response)) {
                setCityOptions(response.data);
            }
        },
        onError: (error) => {
            message.error(error.message);
        },
    });

    const onChangePhoneInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value;
        if (!/^[0-9]*$/.test(value)) {
            value = value.replace(/[^0-9]/g, '');
        }
        if (value.length > 12) {
            value = value.slice(0, 12);
        }
        form.setFieldValue("phoneNo", value);
    };

    const showDrawer = () => {
        setEditingAdmin(null);
        form.resetFields();
        setOpen(true);
    };

    const onEdit = async (record: any) => {
        if (record.userRoleID === 1) {
            setShowPermissions(false);
        }
        else {
            setShowPermissions(true);
        }
        setEditingAdmin(record);
        setOpen(true);

        form.setFieldsValue({
            fullName: record.fullName,
            email: record.email,
            gender: record.gender,
            address: record.address,
            permissions: record.permissionIDs,
            country: record.countryId,
            phoneNo: record.phoneNo,
            phoneNoCountryId: record.phoneNoCountryId,
            phoneNoDialingCode: record.phoneNoDialingCode,
        });

        const stateRes = await fetchStateMutation.mutateAsync({
            countryId: record.countryId,
            searchFilter: ""
        });

        setSelectedCountry({
            value: record.phoneNoCountryId,
            dialingCode: record.phoneNoDialingCode
        });

        const states = stateRes?.data || [];
        setStateOptions(states);

        form.setFieldValue("state", record.stateId);

        const cityRes = await fetchCityMutation.mutateAsync({
            stateId: record.stateId,
            countryId: record.countryId,
            searchFilter: "",
            isFavourite: "",
            isServiceableAttendant: "",
            isServiceableTraveler: "",
        });

        const cities = cityRes?.data || [];
        setCityOptions(cities);

        form.setFieldValue("city", record.cityId);
        form.setFieldValue("userRoleId", record.userRoleID);
    };


    const onFinish = (values: any) => {
        const payload: AdminUserUpsertDto = {
            adminUserId: editingAdmin?.adminUserId ?? "00000000-0000-0000-0000-000000000000",
            fullName: values.fullName,
            email: values.email,
            password: values.password ? values.password : null,
            phoneNo: values.phoneNo,
            phoneNoDialingCode: values.phoneNoDialingCode,
            countryId: values.country,
            stateId: values.state,
            cityId: values.city,
            address: values.address ?? null,
            gender: values.gender ?? null,
            statusId: 1,
            permissionIds: values.permissions ?? [],
            phoneNoCountryId: selectedCountry?.value,
            userRoleID: values.userRoleId
        };

        setFormValues(payload);
        setModalType(editingAdmin ? 'update' : 'add');
        setIsModalVisible(true);
    };

    const handleConfirm = () => {
        if (formValues) {
            setLoading(true);
            addAdminMutation.mutate(formValues);
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setLoading(false);
    };

    const getModalTitle = () => {
        return modalType === 'add' ? 'Confirm Add' : 'Confirm Update';
    };

    const getModalContent = () => {
        if (modalType === 'add') {
            return (
                <p>Are you sure you want to <b>add</b> this admin user? Once added, the user will have access to the system with the selected permissions. <b>Do you wish to proceed?</b></p>
            );
        } else if (modalType === 'update') {
            return (
                <p>Are you sure you want to <b>update</b> the details for <b>{formValues?.fullName}</b>? This action will modify the admin user's information and permissions. <b>Do you wish to proceed?</b></p>
            );
        }
        return null;
    };

    const getPermissionsMutation = useGetAllPermission({
        onSuccess: (res) => {
            groupPermissions(res.data);
        }
    });

    useEffect(() => {
        if (open) {
            getPermissionsMutation.mutate({});
        }
    }, [open]);

    useEffect(() => {
        let role = parseInt(storage.getUserRoleId() ?? "0");
        if (role !== 1) navigate("/");
        fetchCountries();
        fetchAdminList();
        fetchUserRoles();
    }, [pageSize, filters.current]);


    const groupPermissions = (data: any[]) => {
        const grouped: any = {};

        data.filter((item) => item.moduleKey !== "ADMIN_USERS").forEach((item) => {
            if (!grouped[item.moduleKey]) {
                grouped[item.moduleKey] = [];
            }

            grouped[item.moduleKey].push({
                label: item.permissionName || item.actionKey,
                value: item.permissionId,
            });
        });

        const sortedGrouped = Object.keys(grouped)
            .sort((a, b) => a.localeCompare(b))
            .reduce((acc: any, key) => {
                acc[key] = grouped[key];
                return acc;
            }, {});

        setGroupedPermissions(sortedGrouped);
    };

    const onSearch = (value: string) => {
        pageNumber.current = 1;
        setPageSize(10);
        searchValue.current = value;
        fetchAdminList();
    };

    const tabClicked = (key: number) => {
        const status = adminStatuses.find((x: any) => x.statusId === key);
        if (status) {
            filters.current.statusFilter = status.statusName;
            pageNumber.current = 1;
            fetchAdminList();
        }
    };

    const handlePageChange = (newPage: number) => {
        pageNumber.current = newPage;
        fetchAdminList();
    };

    const countryCodeOptions = countries.map(x => ({
        value: x.countryId,
        dialingCode: x.dialingCode,
        label: (
            <div className="flex items-center gap-2 large"  >
                <img src={x.imageUrl} className="w-5" alt="Country Flag" />
                <span>{x.dialingCode}</span>
            </div>
        ),
        searchLabel: `${x.dialingCode} ${x.countryName}`,
    }));

    return (
        <div>
            {/* Header Section */}
            <div className="flex justify-between align-middle pb-5">
                <h2 className="text-2xl font-semibold text-black">Admin Users</h2>
                <div className="flex gap-4 items-center">
                    <div className="w-80 max-w-80">
                        <Search
                            placeholder="Search by name, email, or phone"
                            allowClear
                            size="large"
                            enterButton
                            onSearch={onSearch}
                            className="search-field"
                        />
                    </div>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={showDrawer}
                        size="large"
                        className="h-10 bg-black text-white hover:!border-black hover:!bg-transparent hover:!text-black hover:transition hover:duration-500 hover:ease-in-out"
                    >
                        Add Admin
                    </Button>
                </div>
            </div>

            {/* Tabs Section with Status Filter */}
            <ConfigProvider
                theme={{
                    components: {
                        Tabs: {
                            titleFontSizeLG: 16,
                        },
                    }
                }}
            >
                <Tabs defaultActiveKey="0" onTabClick={(e) => { tabClicked(parseInt(e)) }}>
                    {adminStatuses.map((status: any) => (
                        <TabPane tab={status.statusName} key={status.statusId.toString()}>
                            <Table
                                dataSource={admins}
                                className="userlist-tabs-table"
                                bordered
                                loading={loading}
                                rowKey="adminUserId"
                                pagination={{ pageSize: pageSize, position: ['none'] }}

                            >
                                <Column title="User Name" dataIndex="fullName" key="fullName" align="center" />
                                <Column title="Email" dataIndex="email" key="email" align="center" />
                                <Column title="Role" dataIndex="userRoleName" key="userRoleName" align="center" />
                                <Column
                                    title="Mobile Number"
                                    dataIndex="phoneNo"
                                    key="phoneNo"
                                    align="center"
                                />
                                <Column title="Country" dataIndex="countryName" key="countryName" align="center" />
                                <Column title="City" dataIndex="cityName" key="cityName" align="center" />
                                <Column title="Gender" dataIndex="gender" key="gender" align="center" />
                                <Column
                                    align="center"
                                    title="Status"
                                    dataIndex="statusName"
                                    key="statusName"
                                    render={(tags: string[]) => (
                                        <>
                                            {tags.map((tag) => (
                                                <Tag
                                                    key={tag}
                                                    color={tag === "Active" ? "green" : tag === "Deactivated" ? "red" : "blue"}
                                                    className="m-0 pl-1 pr-1"
                                                >
                                                    {tag.toUpperCase()}
                                                </Tag>
                                            ))}
                                        </>
                                    )}
                                />
                                {status.statusId === 100 && (
                                    <Column title="Deactivated Reason" dataIndex="reason" key="reason" align="center" className="word-wrap-column" />
                                )}
                                <Column title="Created At" dataIndex="createdAt" key="createdAt" align="center" />
                                <Column
                                    align="center"
                                    title="Action"
                                    key="action"
                                    render={(_: any, record: any) => (
                                        <Space size="middle">
                                            <Button
                                                onClick={() => onEdit(record)}
                                                icon={<EditOutlined />}
                                                className="hover:!border-black hover:!text-black"
                                            />
                                            {
                                                (![100].includes(record.statusId)) ?
                                                    <Button
                                                        icon={<DeleteOutlined />}
                                                        className="hover:!border-black hover:!text-black"
                                                        onClick={() => {
                                                            setDeleteId(record.adminUserId);
                                                            setDeleteModalVisible(true);
                                                        }}
                                                    />

                                                    : <></>
                                            }
                                        </Space>
                                    )}
                                />
                            </Table>
                            <div>
                                <Pagination
                                    currentPage={pageNumber.current}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        </TabPane>
                    ))}
                </Tabs>
            </ConfigProvider>

            {/* Form Drawer (Sheet) */}
            <Drawer
                title={
                    <div className="text-xl font-semibold">
                        {editingAdmin ? "Edit Admin User" : "Add New Admin User"}
                    </div>
                }
                placement="right"
                open={open}
                onClose={() => setOpen(false)}
                width="40%"
                styles={{
                    body: { paddingBottom: 80 }
                }}
                footer={
                    <div className="flex flex-row gap-2 justify-end">
                        <Button
                            onClick={() => setOpen(false)}
                            size="large"
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => form.submit()}
                            type="primary"
                            size="large"
                            loading={loading}
                            className="bg-black text-white hover:!border-black hover:!bg-transparent hover:!text-black hover:transition hover:duration-500 hover:ease-in-out"
                        >
                            {editingAdmin ? "Update Admin" : "Add Admin"}
                        </Button>
                    </div>
                }
            >
                <Form
                    layout="vertical"
                    form={form}
                    onFinish={onFinish}
                >
                    {/* Personal Information Section */}
                    <div className="mb-6">
                        <h3 className="text-base font-semibold mb-4 text-gray-700 flex items-center gap-2">
                            <UserOutlined />
                            Personal Information
                        </h3>

                        <Row gutter={[16, 0]}>
                            <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                    name="userRoleId"
                                    label="Role"
                                    rules={[{ required: true, message: "Please select role" }]}
                                >
                                    <Select
                                        showSearch
                                        size="large"
                                        className="font-medium text-neutral-600"
                                        placeholder="Select Role"
                                        optionFilterProp="label"
                                        options={userRoles ? userRoles.map(x => ({
                                            label: x.userRoleName,
                                            value: x.userRoleID
                                        })) : []}
                                        onChange={(roleId) => onRoleChange(roleId)}
                                        allowClear
                                        getPopupContainer={(trigger) => trigger.parentElement}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                    name="fullName"
                                    label="Full Name"
                                    rules={[{ required: true, message: "Please enter full name" }]}
                                >
                                    <Input
                                        size="large"
                                        prefix={<UserOutlined className="text-gray-400" />}
                                        placeholder="Enter full name"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={[16, 0]}>
                            <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                    name="phoneNo"
                                    label="Phone Number"
                                    rules={[
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                const dialingCode = getFieldValue('phoneNoDialingCode');
                                                if (!value && !dialingCode) return Promise.resolve();
                                                if (value && !dialingCode) return Promise.reject(new Error('Please select your country code.'));
                                                if (!value && dialingCode) return Promise.reject(new Error('Please enter your phone number.'));
                                                if (value && (value.length < 7 || value.length > 12))
                                                    return Promise.reject(new Error("Phone number must be between 7 and 12 digits."));
                                                return Promise.resolve();
                                            },
                                        }),
                                    ]}
                                >
                                    <Input
                                        addonBefore={
                                            <Form.Item name="phoneNoCountryId" noStyle>
                                                <Select
                                                    className="w-[100px] h-[40px]"
                                                    options={countryCodeOptions}
                                                    showSearch
                                                    filterOption={(input, option: any) =>
                                                        option?.searchLabel?.toLowerCase().includes(input.toLowerCase())
                                                    }
                                                    onChange={(value, option: any) => {
                                                        setSelectedCountry(option);
                                                        form.setFieldValue("phoneNoDialingCode", option.dialingCode);
                                                    }}
                                                    getPopupContainer={(trigger) => trigger.parentElement}
                                                />
                                            </Form.Item>
                                        }
                                        onChange={onChangePhoneInput}
                                        placeholder="Enter phone number"
                                        maxLength={12}
                                        size="large"
                                    />
                                </Form.Item>
                                <Form.Item name="phoneNoDialingCode" hidden>
                                    <Input size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                    name="email"
                                    label="Email"
                                    rules={[
                                        { required: true, message: "Please enter email" },
                                        { type: "email", message: "Please enter a valid email" }
                                    ]}
                                >
                                    <Input
                                        size="large"
                                        prefix={<MailOutlined className="text-gray-400" />}
                                        placeholder="Enter email address"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={[16, 0]}>
                            <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                    name="country"
                                    label="Country"
                                    rules={[{ required: true, message: "Please select country" }]}
                                >
                                    <Select
                                        showSearch
                                        size="large"
                                        className="font-medium text-neutral-600"
                                        placeholder="Select country"
                                        optionFilterProp="label"
                                        options={countries ? countries.map(x => ({
                                            label: x.countryName,
                                            value: x.countryId
                                        })) : []}
                                        onChange={onCountryChange}
                                        allowClear
                                        suffixIcon={<GlobalOutlined className="text-gray-400" />}
                                        getPopupContainer={(trigger) => trigger.parentElement}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                    name="state"
                                    label="State"
                                    rules={[{ required: true, message: "Please select state" }]}
                                >
                                    <Select
                                        showSearch
                                        size="large"
                                        className="font-medium text-neutral-600"
                                        placeholder="Select state"
                                        optionFilterProp="label"
                                        options={stateOptions ? stateOptions.map(x => ({
                                            label: x.stateName,
                                            value: x.stateId
                                        })) : []}
                                        onChange={onStateChange}
                                        allowClear
                                        getPopupContainer={(trigger) => trigger.parentElement}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={[16, 0]}>
                            <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                    name="city"
                                    label="City"
                                    rules={[{ required: true, message: "Please select city" }]}
                                >
                                    <Select
                                        showSearch
                                        size="large"
                                        className="font-medium text-neutral-600"
                                        placeholder="Select city"
                                        optionFilterProp="label"
                                        options={cityOptions ? cityOptions.map(x => ({
                                            label: x.cityName,
                                            value: x.cityId
                                        })) : []}
                                        allowClear
                                        getPopupContainer={(trigger) => trigger.parentElement}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={24} md={12}>
                                <Form.Item name="gender" label="Gender" rules={[{ required: true, message: "Please select gender" }]}>
                                    <Select placeholder="Select gender" size="large" allowClear getPopupContainer={(trigger) => trigger.parentElement}>
                                        <Select.Option value="Male">Male</Select.Option>
                                        <Select.Option value="Female">Female</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                    name="password"
                                    label={editingAdmin ? "New Password (leave blank to keep old)" : "Password"}
                                    rules={
                                        editingAdmin
                                            ? []
                                            : [{ required: true, message: "Please enter password" }]
                                    }
                                >
                                    <Input.Password
                                        size="large"
                                        prefix={<LockOutlined className="text-gray-400" />}
                                        placeholder="Enter password"
                                    />
                                </Form.Item>

                            </Col>
                        </Row>

                        {/* Address Full Row */}
                        <Row gutter={[16, 0]}>
                            <Col xs={24}>
                                <Form.Item name="address" label="Address" rules={[{ required: true, message: "Please enter full address" }]}>
                                    <Input.TextArea
                                        placeholder="Enter full address"
                                        autoSize={{ minRows: 4, maxRows: 6 }}
                                        style={{ resize: "none" }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    <Divider />

                    {showPermissions && (

                        <>
                            <div className="mb-6">
                                <h3 className="text-base font-semibold mb-4 text-gray-700">
                                    Permissions & Access Control
                                </h3>

                                <div className="mb-2">
                                    <span className="text-sm text-gray-600">
                                        Select the permissions for this admin user
                                    </span>
                                </div>

                                <Form.Item name="permissions">
                                    <PermissionsSelector groupedPermissions={groupedPermissions} />
                                </Form.Item>
                            </div>
                        </>

                    )}
                </Form>
            </Drawer>

            {/* Confirmation Modal */}
            <Modal
                title={<span className="custom-modal-title">{getModalTitle()}</span>}
                open={isModalVisible}
                onOk={handleConfirm}
                onCancel={handleCancel}
                closeIcon={<CloseOutlined className="text-gray-500 hover:text-black" />}
                confirmLoading={loading}
                footer={[
                    <Button key="back" onClick={handleCancel} className="hover:!text-black hover:!border-black" disabled={loading}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        onClick={handleConfirm}
                        loading={loading}
                        className="bg-black text-white hover:bg-transparent hover:!text-black hover:!border-black"
                    >
                        Confirm
                    </Button>
                ]}
            >
                {getModalContent()}
            </Modal>

            <Modal
                title={<span className="custom-modal-title">Confirm Delete</span>}
                open={deleteModalVisible}
                onOk={confirmDelete}
                onCancel={() => {
                    setDeleteModalVisible(false);
                    setDeleteId(null);
                }}
                closeIcon={<CloseOutlined className="text-gray-500 hover:text-black" />}
                confirmLoading={deleteAdminMutation.isPending}
                footer={[
                    <Button
                        key="back"
                        onClick={() => {
                            setDeleteModalVisible(false);
                            setDeleteId(null);
                        }}
                        disabled={deleteAdminMutation.isPending}
                        className="hover:!text-black hover:!border-black"
                    >
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        loading={deleteAdminMutation.isPending}
                        onClick={confirmDelete}
                        className="!bg-black !text-white hover:!bg-transparent hover:!text-black hover:!border-black"
                    >
                        Delete
                    </Button>

                ]}
            >
                <p>
                    Are you sure you want to permanently <b>delete</b> this admin user?
                    This action cannot be undone.
                </p>
            </Modal>

        </div>
    );
};

export default AdminUsers;